import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the Merakee circle. You are on the list.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="grain border-t border-[#A58343]/20 bg-[#F4EEE3]">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="grid gap-16 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="font-serif text-2xl font-light uppercase tracking-[0.3em] text-[#262019]">
              Merakee
            </p>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-[#7C7163]">
              Real diamonds, royal craft. Heirloom polki sets, necklaces, bracelets
              and rings, brought to you through private consultations — wherever you are.
            </p>
            <div className="mt-8 flex gap-5">
              <a href="https://wa.me/8801713044029?text=Hello%20Merakee%2C%20I%20would%20like%20to%20enquire%20about%20your%20jewellery."
                 target="_blank" rel="noreferrer" aria-label="WhatsApp"
                 data-testid="social-whatsapp" className="text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">
                <MessageCircle size={18} strokeWidth={1.5} />
              </a>
              <a href="tel:+8801713044029" aria-label="Call us"
                 data-testid="social-phone" className="text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">
                <Phone size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div>
            <p className="overline">Explore</p>
            <div className="mt-6 flex flex-col gap-4">
              <Link to="/collections" data-testid="footer-collections" className="text-sm text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">Collections</Link>
              <Link to="/about" data-testid="footer-about" className="text-sm text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">Our Story</Link>
              <Link to="/journal" data-testid="footer-journal" className="text-sm text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">Journal</Link>
              <Link to="/contact" data-testid="footer-contact" className="text-sm text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">Contact</Link>
            </div>
          </div>

          <div>
            <p className="overline">Collections</p>
            <div className="mt-6 flex flex-col gap-4">
              <Link to="/collections?category=polki-sets" data-testid="footer-polki" className="text-sm text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">Polki Sets</Link>
              <Link to="/collections?category=necklaces" data-testid="footer-necklaces" className="text-sm text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">Necklaces</Link>
              <Link to="/collections?category=bracelets" data-testid="footer-bracelets" className="text-sm text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">Bracelets</Link>
              <Link to="/collections?category=rings" data-testid="footer-rings" className="text-sm text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">Rings</Link>
              <Link to="/collections?category=earrings" data-testid="footer-earrings" className="text-sm text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">Earrings</Link>
            </div>
          </div>

          <div>
            <p className="overline">The Merakee Circle</p>
            <p className="mt-6 text-sm leading-relaxed text-[#7C7163]">
              New pieces, private previews and stories from the atelier. Once a month, never more.
            </p>
            <form onSubmit={subscribe} className="mt-6" data-testid="newsletter-form">
              <div className="flex items-end gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  data-testid="newsletter-email-input"
                  className="field text-base"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="newsletter-submit-button"
                  className="shrink-0 border-b border-[#A58343] pb-3 text-xs uppercase tracking-[0.25em] text-[#A58343] transition-colors duration-300 hover:text-[#8C6D33]"
                >
                  {sending ? "Joining…" : "Join"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="select-none overflow-hidden border-t border-[#A58343]/10 px-6 pb-6">
        <p className="mt-10 text-center font-serif text-[11vw] font-light uppercase leading-none tracking-[0.15em] text-[#A58343]/10">
          Merakee
        </p>
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 border-t border-[#A58343]/10 pt-8 text-xs tracking-[0.2em] text-[#7C7163] sm:flex-row">
          <p data-testid="footer-copyright">© 2026 MERAKEE FINE JEWELLERY</p>
          <p className="text-center text-[10px] leading-relaxed tracking-[0.12em] sm:text-xs sm:tracking-[0.2em]">
            REAL DIAMONDS · DOCUMENTED WHERE IT QUALIFIES · PRIVATE CONSULTATIONS
          </p>
        </div>
      </div>
    </footer>
  );
};
